import { useEffect, useRef, useState, type ReactNode } from 'react';

type RevealProps = {
  children: ReactNode;
  className?: string;
  delay?: number;
  variant?: 'up' | 'fade' | 'clip';
  threshold?: number;
};

/**
 * Reveals its children when they enter the viewport (once).
 */
export default function Reveal({
  children,
  className = '',
  delay = 0,
  variant = 'up',
  threshold = 0.15,
}: RevealProps) {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = elementRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
          }
        });
      },
      { threshold, rootMargin: '0px 0px -60px 0px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);

  const variantClass =
    variant === 'fade' ? 'reveal-fade' : variant === 'clip' ? 'reveal-clip' : 'reveal';

  return (
    <div
      ref={elementRef}
      className={`${variantClass} ${className}`}
      data-visible={visible ? 'true' : 'false'}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}